import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { GoodIcon } from '@net0-calc/common/assets/svg';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';

gsap.registerPlugin(ScrollTrigger);

const translationPrefix = 'personal.pages.result.reductionOpportunities';

export const ReductionOpportunities = () => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const blocksAnimationTimeLine = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        scrub: 1,
        start: 'top 500px',
        end: 'bottom bottom',
      },
    });
    blocksAnimationTimeLine
      .from(containerRef.current, {
        opacity: 0.2,
      })
      .to(containerRef.current, {
        opacity: 1,
        duration: 0.7,
      });
  }, []);

  return (
    <Root ref={containerRef}>
      <Title>
        <EmojiContainer>🌱</EmojiContainer>{' '}
        <span dangerouslySetInnerHTML={{ __html: t(`${translationPrefix}.title`) }}></span>
      </Title>
      <StepWrap>
        <StepTitleWrap>
          <GoodIcon />
          {t(`${translationPrefix}.sustainableHospitalityOptions.title`)}
        </StepTitleWrap>
        <StepParagraph>{t(`${translationPrefix}.sustainableHospitalityOptions.description`)}</StepParagraph>
      </StepWrap>
      <StepWrap>
        <StepTitleWrap>
          <GoodIcon />
          {t(`${translationPrefix}.stayInPrivateAccomodation.title`)}
        </StepTitleWrap>
        <StepParagraph>{t(`${translationPrefix}.stayInPrivateAccomodation.description`)}</StepParagraph>
      </StepWrap>
      <StepWrap>
        <StepTitleWrap>
          <GoodIcon />
          {t(`${translationPrefix}.walkOrCycleInMonaco.title`)}
        </StepTitleWrap>
        <StepParagraph>{t(`${translationPrefix}.walkOrCycleInMonaco.description`)}</StepParagraph>
      </StepWrap>
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 845px;
  margin-bottom: 60px;
`;

const Title = styled.div`
  ${TYPOGRAPHY.titleSemibold32}
  color: var(--color6);
  margin-bottom: 32px;
  display: flex;
`;

const StepWrap = styled.div`
  width: 100%;
  &:not(:last-child) {
    margin-bottom: 30px;
  }
`;

const StepTitleWrap = styled.div`
  $${TYPOGRAPHY.boydRegular20}
  color: var(--color6);

  display: flex;
  align-items: center;
  margin-bottom: 12px;

  & svg {
    margin-right: 12px;
  }
`;

const StepParagraph = styled.div`
  ${TYPOGRAPHY.bodyRegular16}
  color: var(--color9);
  margin-left: 22px;

  @media ${DEVICE.laptop} {
    margin-left: 44px;
  }
`;

const emojiPulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const EmojiContainer = styled.div`
  ${TYPOGRAPHY.titleSemibold32}
  cursor: pointer;
  margin-right: 8px;
  &:hover {
    animation: ${emojiPulseAnimation} 0.2s ease-in normal;
  }
`;
